import * as React from 'react';
import { Button, getIntrinsicElementProps, slot, Tooltip, useFluent } from '@fluentui/react-components';
import type {
  GroundingMenuSubmenuHeaderProps,
  GroundingMenuSubmenuHeaderState,
} from './GroundingMenuSubmenuHeader.types';

import {
  ArrowLeft20Filled,
  ArrowLeft20Regular,
  ArrowRight20Filled,
  ArrowRight20Regular,
  bundleIcon,
} from '@fluentui/react-icons';

const ArrowLeftIcon = bundleIcon(ArrowLeft20Filled, ArrowLeft20Regular);
const ArrowRightIcon = bundleIcon(ArrowRight20Filled, ArrowRight20Regular);

/**
 * Create the state required to render GroundingMenuSubmenuHeader.
 *
 * The returned state can be modified with hooks such as useGroundingMenuSubmenuHeaderStyles_unstable,
 * before being passed to renderGroundingMenuSubmenuHeader_unstable.
 *
 * @param props - props from this instance of GroundingMenuSubmenuHeader
 * @param ref - reference to root HTMLElement of GroundingMenuSubmenuHeader
 */
export const useGroundingMenuSubmenuHeader_unstable = (
  props: GroundingMenuSubmenuHeaderProps,
  ref: React.Ref<HTMLDivElement>,
): GroundingMenuSubmenuHeaderState => {
  const { dir } = useFluent();

  return {
    components: {
      root: 'div',
      backButton: Button,
      backButtonTooltip: Tooltip,
      primaryText: 'div',
      secondaryText: 'div',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref,
        ...props,
      }),
      { elementType: 'div' },
    ),
    backButton: slot.optional(props.backButton, {
      defaultProps: {
        appearance: 'transparent',
        icon: dir === 'rtl' ? <ArrowRightIcon /> : <ArrowLeftIcon />,
        role: 'menuitem',
      },
      elementType: Button,
      renderByDefault: true,
    }),
    backButtonTooltip: slot.optional(props.backButtonTooltip, {
      defaultProps: { relationship: 'label' },
      elementType: Tooltip,
    }),
    primaryText: slot.optional(props.primaryText, { elementType: 'div' }),
    secondaryText: slot.optional(props.secondaryText, { elementType: 'div' }),
  };
};
